import {computed, inject, Injectable, Injector, signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {MonAbonnementService} from '../api/mon-abonnement/mon-abonnement.service';
import type {MonAbonnement} from '../api/mon-abonnement/mon-abonnement.service';

const SUPPORT: MonAbonnement = {
  nextAction: 'contact_support',
  managementUrl: null,
  subscriptionFound: false,
  reference: null,
  offerCode: null,
  status: null,
  indisponible: true,
};

/**
 * Store Signals de l'abonnement du citoyen (action « gérer » ou « support »), partagé entre le bandeau
 * d'expiration et la page Mon abonnement : un seul appel réseau. En mémoire uniquement (le lien porte un
 * jeton portail : jamais en localStorage). Fail-safe : toute erreur ⇒ action support.
 *
 * `MonAbonnementService` (donc `HttpClient`) est résolu paresseusement dans `load()`.
 */
@Injectable({providedIn: 'root'})
export class MonAbonnementStore {
  private readonly injector = inject(Injector);

  private readonly _abonnement = signal<MonAbonnement | null>(null);
  private inFlight: Promise<void> | null = null;

  /** Abonnement chargé, ou null tant que le chargement n'a pas abouti. */
  readonly abonnement = this._abonnement.asReadonly();
  readonly loaded = computed(() => this._abonnement() !== null);
  /** Lien de gestion à ouvrir, ou null si l'action proposée est le support. */
  readonly managementUrl = computed(() => {
    const a = this._abonnement();
    return a?.nextAction === 'manage_subscription' ? a.managementUrl : null;
  });

  /** Charge l'abonnement si besoin (idempotent, dédupliqué). */
  async ensureLoaded(): Promise<void> {
    if (this.loaded()) return;
    this.inFlight ??= this.load().finally(() => (this.inFlight = null));
    await this.inFlight;
  }

  /** Force un rechargement (ex. après un changement de droits). */
  async reload(): Promise<void> {
    this._abonnement.set(null);
    await this.ensureLoaded();
  }

  clear(): void {
    this._abonnement.set(null);
  }

  private async load(): Promise<void> {
    try {
      const api = this.injector.get(MonAbonnementService);
      const res = await firstValueFrom(api.getMonAbonnement());
      this._abonnement.set(res?.data ?? SUPPORT);
    } catch {
      this._abonnement.set(SUPPORT);
    }
  }
}
